import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ChevronDown, ChevronUp, Info, Plus, Search, SlidersHorizontal } from "lucide-react";
import { useLiveSupportRequest } from "src/hooks/useLiveSupportRequest";
import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import Modal from "src/components/ui/Modal";
import MailContent from "src/components/common/MailContent";
import SupportTicketCreateModal from "./SupportTicketCreateModal";

import { toast } from "react-toastify";
import { formatDateTime } from "src/utils/date-time";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as supportRequestActions from "src/redux/slices/supportRequestSlice";

const STATUS_OPTIONS = [
  { label: "Open", value: "OPEN" },
  { label: "Closed", value: "CLOSED" },
];

const SupportRequestsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { list, detail, updateStatus, reply } = useAppSelector(
    (state) => state.supportRequest
  );
  const [limit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const hasActiveFilters = Boolean(search || statusFilter);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Modal state for viewing a support request in a conversation view
  const [openRequestModal, setOpenRequestModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null
  );
  const [selectedRequestRow, setSelectedRequestRow] = useState<any>(null);

  useLiveSupportRequest(selectedRequestId, "admin", reply.loading || detail.loading);

  const openTicket = (id: string, fallbackRow?: any) => {
    dispatch(supportRequestActions.resetSupportRequestDetail());
    setSelectedRequestId(id);
    setSelectedRequestRow(fallbackRow || null);
    setOpenRequestModal(true);
    dispatch(supportRequestActions.getSupportRequestByIdAdminRequest(id));
  };

  const handleOpenRequestModal = (row: any) => {
    openTicket(row._id || row.id, row);
  };

  const handleCloseRequestModal = () => {
    setOpenRequestModal(false);
    setSelectedRequestId(null);
    setSelectedRequestRow(null);
    dispatch(supportRequestActions.resetSupportRequestDetail());
  };

  const fetchList = (page = currentPage) =>
    dispatch(
      supportRequestActions.getSupportRequestsListAdminRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      })
    );

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Filters reset back to page 1; page-to-page navigation fetches directly
  // via TableV2's onPageChange instead of going through this effect, so
  // changing a filter mid-page doesn't double-fetch.
  useEffect(() => {
    setCurrentPage(1);
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, limit]);

  // Deep link from a notification: /admin/support?ticketId=<id>
  useEffect(() => {
    const ticketId = searchParams.get("ticketId");
    if (ticketId) {
      openTicket(ticketId);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleReply = (msg: string) => {
    if (!selectedRequestId) return;
    dispatch(
      supportRequestActions.replySupportRequestAdminRequest({
        _id: selectedRequestId,
        message: msg,
      })
    );
  };

  useEffect(() => {
    if (reply.data) {
      dispatch(supportRequestActions.resetReplySupportRequest());
      fetchList();
    } else if (reply.error) {
      toast.error(`Error: ${reply.error}`);
      dispatch(supportRequestActions.resetReplySupportRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reply.data, reply.error]);

  const handleStatusChange = (nextStatus: "OPEN" | "CLOSED") => {
    if (!selectedRequestId) return;
    dispatch(
      supportRequestActions.updateSupportRequestStatusAdminRequest({
        _id: selectedRequestId,
        status: nextStatus,
      })
    );
  };

  useEffect(() => {
    if (updateStatus.data) {
      toast.success(
        updateStatus.data.status === "CLOSED" ? "Chat closed." : "Chat reopened."
      );
      dispatch(supportRequestActions.resetUpdateSupportRequestStatusAdmin());
      fetchList();
    } else if (updateStatus.error) {
      toast.error(`Error: ${updateStatus.error}`);
      dispatch(supportRequestActions.resetUpdateSupportRequestStatusAdmin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateStatus.data, updateStatus.error]);

  const normalizeStatus = (status: string) =>
    status?.toString().trim().toLowerCase();

  const modalRequest = useMemo(
    () => (detail.data?._id === selectedRequestId ? detail.data : selectedRequestRow),
    [detail.data, selectedRequestRow, selectedRequestId]
  );

  return (
    <Layout
      type="admin"
      meta={{
        title: "Support Requests | Invyntra",
        description: "Track and resolve all support requests in one place.",
        keywords: "support, requests, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Info size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Support Requests
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage and resolve support requests from users.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button rounded="full" onClick={() => setCreateModalOpen(true)}>
              <Plus size={16} className="mr-1" /> Raise Ticket
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search tickets..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />
            </div>
          )}
        </Card>

        <Card>
          <TableV2
            columns={[
              {
                key: "requestId",
                header: "Request ID",
                accessor: ({ key, row }) => (
                  <button
                    className="font-mono text-indigo-600 hover:underline"
                    onClick={() => handleOpenRequestModal(row)}
                  >
                    #{row[key]}
                  </button>
                ),
              },
              { key: "name", header: "Name" },
              { key: "email", header: "Email" },
              { key: "mobile", header: "Mobile" },
              {
                key: "message",
                header: "Message",
                accessor: ({ key, row }) => (
                  <div className="max-w-xs truncate">{row[key]}</div>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => {
                  const status = normalizeStatus(
                    row[key] || row?.requestStatus || row?.supportStatus || ""
                  );
                  return (
                    <Badge
                      text={status ? status.toUpperCase() : "OPEN"}
                      color={status === "closed" ? "green" : "yellow"}
                    />
                  );
                },
              },
              {
                key: "createdAt",
                header: "Created",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD MMM YYYY")}</div>
                ),
              },
            ]}
            data={list?.data?.data}
            pagination={list?.data?.pagination}
            onPageChange={(page) => {
              setCurrentPage(page);
              fetchList(page);
            }}
          />
          {openRequestModal && (
            <Modal
              open={openRequestModal}
              onClose={handleCloseRequestModal}
              title={`Support Request #${modalRequest?.requestId || ""}`}
              size="2xl"
              bodyClassName="flex min-h-0 flex-1 overflow-hidden"
            >
              <MailContent
                key={selectedRequestId}
                request={modalRequest}
                replyResult={reply.data}
                replyError={reply.error}
                onReply={handleReply}
                replying={reply.loading}
                viewerType="admin"
                onStatusChange={handleStatusChange}
                statusChanging={updateStatus.loading}
              />
            </Modal>
          )}
        </Card>
      </div>

      {createModalOpen && (
        <SupportTicketCreateModal
          open={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            fetchList(1);
          }}
        />
      )}
    </Layout>
  );
};

export default SupportRequestsPage;
